import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import AppContext from '../../context/AppContext';
import { useNavigation } from '../../context/NavigationContext';
import { database, ref, get } from "../../FirebaseConfig";
// Importer les icônes individuellement au lieu d'un import groupé
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import ArrowDownUp from 'lucide-react/dist/esm/icons/arrow-down-up';
import Settings from 'lucide-react/dist/esm/icons/settings';
import HelpCircle from 'lucide-react/dist/esm/icons/help-circle';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Heart from 'lucide-react/dist/esm/icons/heart';
import Users from 'lucide-react/dist/esm/icons/users';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import Bell from 'lucide-react/dist/esm/icons/bell';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Activity from 'lucide-react/dist/esm/icons/activity';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Target from 'lucide-react/dist/esm/icons/target';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Clock from 'lucide-react/dist/esm/icons/clock';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import Wallet from 'lucide-react/dist/esm/icons/wallet';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card';
import PieChart from 'lucide-react/dist/esm/icons/pie-chart';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import Gift from 'lucide-react/dist/esm/icons/gift';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import Menu from 'lucide-react/dist/esm/icons/menu';
import X from 'lucide-react/dist/esm/icons/x';
import Check from 'lucide-react/dist/esm/icons/check';
import ShoppingBag from 'lucide-react/dist/esm/icons/shopping-bag';
import Home from 'lucide-react/dist/esm/icons/home';

// Importer notre version sécurisée de l'icône Lock qui évite les problèmes de constructeur
import { Lock, ShieldLock } from '../../lucide-icon-patch';
import { useNavigate } from "react-router-dom";
// Désactiver complètement Telegram WebApp
import { NoTelegram } from '../../no-telegram';
import ComingSoonAlert from './ComingSoonAlert';

// Renommer l'icône Home pour garder la compatibilité
const DashboardIcon = Home;


// Custom carousel component with smooth animations
const Carousel = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const carouselRef = useRef(null);
  
  const next = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((current) => 
      current === React.Children.count(children) - 1 ? 0 : current + 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  }, [children, isAnimating]);

  const prev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((current) => 
      current === 0 ? React.Children.count(children) - 1 : current - 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  }, [children, isAnimating]);

  const goToSlide = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);
  
  // Touch swipe handlers
  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsSwiping(true);
  };
  
  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        next();
      } else {
        prev();
      }
      setIsSwiping(false);
    }
  };
  
  const handleTouchEnd = () => {
    setIsSwiping(false);
  };

  return (
    <div className="relative" ref={carouselRef}>
      <div 
        className="overflow-hidden rounded-2xl"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {children}
        </div>
      </div>
      <button 
        onClick={prev}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-200 z-10"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 text-gray-700" />
      </button>
      <button 
        onClick={next}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all duration-200 z-10"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 text-gray-700" />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {React.Children.map(children, (_, index) => (
          <button
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white scale-125' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Quick Action button component with animation
const QuickActionButton = ({ icon, label, onClick, gradient }) => (
  <button
    onClick={onClick}
    className="group relative flex flex-col items-center p-4 rounded-2xl hover:bg-gray-50 transition-all duration-300 transform hover:-translate-y-1"
  >
    <div className={`p-3 bg-gradient-to-br ${gradient} rounded-xl mb-3 text-white group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}>
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <span className="text-sm font-medium text-gray-800">{label}</span>
  </button>
);

// Enhanced balance card component with animation
const BalanceCard = ({ userInfos, onWithdrawClick }) => {
  const { navigateTo } = useNavigation();
  if (!userInfos) {
    return null; // Ne pas afficher la carte si userInfos est null/undefined
  }
  
  const balance = userInfos?.type_plan === "payant" 
    ? userInfos?.balancePayant || 0
    : userInfos?.balanceGratuit || 0;
  
  // Déterminer les couleurs en fonction du type de plan
  const gradientColors = userInfos?.type_plan === "payant" 
    ? "from-purple-600 via-indigo-600 to-blue-700" 
    : "from-blue-600 via-blue-600 to-teal-600";
  
  return (
    <div className="px-6 mb-8">
      <div className={`bg-gradient-to-br ${gradientColors} rounded-3xl p-6 shadow-2xl relative overflow-hidden`}>
        {/* Decorative elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-20 -left-10 w-60 h-60 bg-indigo-400/20 rounded-full blur-xl" />
        <div className="absolute top-0 left-0 w-full h-full bg-white/5 backdrop-blur-sm" />
        
        {/* Card content */}
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="bg-white/20 p-2 rounded-lg mr-3">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Balance Disponible</p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-2">
              <RefreshCw className="w-4 h-4 text-white" />
            </div>
          </div>
          
          <div className="flex justify-between items-end mt-4 mb-6">
            <div>
              <h2 className="text-5xl font-bold text-white mb-1 flex items-baseline">
                {balance.toLocaleString()}
                <span className="text-sm font-medium text-blue-100 ml-2">F CFA</span>
              </h2>
              {userInfos?.type_plan === "payant" ? (
                <span className="inline-block bg-purple-400/20 text-purple-100 text-xs px-3 py-1 rounded-full">
                  Plan Premium
                </span>
              ) : (
                <span className="inline-block bg-teal-400/20 text-teal-100 text-xs px-3 py-1 rounded-full">
                  Plan Gratuit
                </span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 w-full mt-6">
            <button 
              onClick={onWithdrawClick}
              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all duration-300 transform hover:-translate-y-1 shadow-lg ${
                userInfos?.type_plan === "gratuit" 
                  ? "bg-white/10 backdrop-blur-md text-white border border-white/30 hover:bg-white/20"
                  : "bg-gray-300/50 text-gray-400 cursor-not-allowed"
              }`}
              disabled={false}
            >
              <ArrowDownUp className="w-4 h-4" />
              Retrait {userInfos?.type_plan !== "gratuit" && <ShieldLock className="w-3 h-3 ml-1" />}
            </button>
            <button 
             onClick={() => navigateTo('/transactions/history')}
              className="flex items-center justify-center gap-2 bg-white py-3 px-4 rounded-xl font-medium text-blue-700 hover:bg-blue-50 transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
            >
              <PieChart className="w-4 h-4" />
              Historique
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced offer card component with animation
const OfferCard = ({ title, description, features, duration, gradient, icon, onDiscoverClick, price }) => (
  <div className="w-full flex-shrink-0 flex-grow-0 px-2">
    <div className={`bg-gradient-to-br ${gradient} p-6 rounded-2xl text-white h-full shadow-lg relative overflow-hidden`}>
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-x-10 -translate-y-16" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-x-10 translate-y-16" />
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center mb-2">
              <div className="p-2 bg-white/10 rounded-xl mr-3">
                {icon}
              </div>
              <h3 className="font-bold text-xl">{title}</h3>
            </div>
            <p className="text-white/90 text-sm">{description}</p>
          </div>
          
          {price && (
            <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
              {price}
            </div>
          )}
        </div>
        
        <ul className="space-y-3 mb-6">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-3 text-sm bg-white/10 p-2 rounded-lg">
              <div className="w-4 h-4 bg-white/20 rounded-full flex items-center justify-center p-2">
                <Check className="w-3 h-3 text-white" />
              </div>
              {feature}
            </li>
          ))}
        </ul>
        
        <div className="flex justify-between items-center">
          <div className="text-sm bg-white/10 px-3 py-1 rounded-full">
            <span className="font-medium">{duration}</span>
          </div>
          <button 
            onClick={onDiscoverClick}
            className="bg-white text-gray-900 px-5 py-2 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Découvrir
          </button>
        </div>
      </div>
    </div>
  </div>
);

// New component for statistics
const StatCard = ({ title, value, icon, color, fullWidth }) => (
  <div className={`bg-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${fullWidth ? 'col-span-2' : ''}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 ${color} rounded-lg`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
    </div>
    <p className="text-2xl font-bold text-gray-800 ml-2 mt-1">{value}</p>
  </div>
);

// New component for transaction history
const TransactionItem = ({ amount, type, date, status }) => {
  const isIncome = type === 'income';
  return (
    <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${isIncome ? 'bg-green-100' : 'bg-blue-100'}`}>
          {isIncome ? (
            <ArrowDownUp className="w-4 h-4 text-green-600" />
          ) : (
            <CreditCard className="w-4 h-4 text-blue-600" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-800">{isIncome ? 'Réception' : 'Paiement'}</p>
          <p className="text-xs text-gray-500">{date}</p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isIncome ? 'text-green-600' : 'text-gray-800'}`}>
          {isIncome ? '+' : '-'}{amount} FCFA
        </p>
        <p className={`text-xs ${
          status === 'Complété' ? 'text-green-600' : 
          status === 'En attente' ? 'text-orange-500' : 'text-red-500'
        }`}>
          {status}
        </p>
      </div>
    </div>
  );
};

// En-tête de l'application - version simplifiée
const AppTitle = ({ userInfos }) => {
  return null; // Suppression complète de l'en-tête
};

// Main IndexEspace component
const IndexEspace = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigation();
  const [scrolled, setScrolled] = useState(false);
  // Utilisation des données du contexte
  const { userInfos, id, userProducts } = useContext(AppContext);
  const [showAlert, setShowAlert] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Fonction pour charger et traiter les données de transaction
  const loadTransactionData = useCallback(async () => {
    if (!id) return { transactions: [], successCount: 0, totalRevenue: 0 };
    
    try {
      // Référence directe à transaction-abonnement dans Firebase
      const transactionRef = ref(database, `transaction-abonnement`);
      const snapshot = await get(transactionRef);
      
      if (!snapshot.exists()) {
        return { transactions: [], successCount: 0, totalRevenue: 0 };
      }
      
      const transactionData = snapshot.val();
      const transactionsArray = [];
      let successCount = 0;
      let totalRevenue = 0;
      
      // Parcourir l'objet transaction-abonnement
      Object.entries(transactionData).forEach(([transactionId, transaction]) => {
        // Ne conserver que les transactions où l'idVendeur correspond à l'utilisateur connecté
        if (transaction.idVendeur && transaction.idVendeur.toString() === id?.toString()) {
          // Compter les transactions réussies et calculer le revenu total
          if (transaction.status === 'success') {
            successCount++;
            // Ajouter le montant au revenu total
            totalRevenue += Number(transaction.amount || 0);
          }
          
          transactionsArray.push({
            id: transactionId,
            amount: transaction.amount?.toLocaleString() || '0',
            type: 'income', // Toutes les transactions sont des revenus pour le vendeur
            date: transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('fr-FR', {
              day: '2-digit', 
              month: 'short', 
              year: 'numeric'
            }) : 'Date inconnue',
            status: transaction.status === 'success' ? 'Complété' : 
                    transaction.status === 'pending' ? 'En attente' : 'Échec',
            idProduit: transaction.idProduit,
            idClient: transaction.idClient,
            duration: transaction.duration
          });
        }
      });
      
      // Trier par date décroissante (plus récent d'abord)
      transactionsArray.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
      
      // Ne prendre que les 4 premières transactions pour l'affichage
      return { 
        transactions: transactionsArray.slice(0, 4),
        successCount: successCount,
        totalRevenue: totalRevenue
      };
    } catch (error) {
      console.error("Erreur lors du chargement des transactions:", error);
      return { transactions: [], successCount: 0, totalRevenue: 0 };
    }
  }, [id]);
  
  // État pour stocker les transactions, le nombre de transactions réussies et le revenu total
  const [transactions, setTransactions] = useState([]);
  const [successfulTransactions, setSuccessfulTransactions] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  
  // Charger les transactions au chargement du composant
  useEffect(() => {
    async function fetchTransactions() {
      const result = await loadTransactionData();
      setTransactions(result.transactions);
      setSuccessfulTransactions(result.successCount);
      setTotalRevenue(result.totalRevenue);
    }
    
    fetchTransactions();
  }, [loadTransactionData]);

  // Action route mapping
  const handleNavigation = useCallback((label) => {
    const routes = {
      "Donation": "/donation",
      "Produits": "/ProductsList",
      "Groupes": "/GroupeListe",
      "Paramètres": "/PaymentsSheetsIndex",
      "Support": "/CustomerSupport",
      "FAQ": "/IndexFaq",
      "Statistiques": "/Statistics",
      "Calendrier": "/Calendar",
    };
    return routes[label] || "/";
  }, []);

  // Quick actions data with gradients
  const quickActions = useMemo(() => [
    { 
      icon: <Gift className="text-white" />, 
      label: "Donation",
      gradient: "from-rose-500 to-pink-600" 
    },
    { 
      icon: <MessageSquare className="text-white" />, 
      label: "Produits",
      gradient: "from-blue-500 to-indigo-600" 
    },
    { 
      icon: <Users className="text-white" />, 
      label: "Groupes",
      gradient: "from-green-500 to-teal-600" 
    },
    { 
      icon: <BarChart3 className="text-white" />, 
      label: "Statistiques",
      gradient: "from-amber-500 to-orange-600" 
    },
    { 
      icon: <Calendar className="text-white" />, 
      label: "Calendrier",
      gradient: "from-purple-500 to-indigo-600" 
    },
    { 
      icon: <HelpCircle className="text-white" />, 
      label: "Support",
      gradient: "from-cyan-500 to-sky-600" 
    }
  ], []);

  // Premium offers data
  const premiumOffers = useMemo(() => [
    {
      title: "Accès VIP",
      description: "Fonctionnalités exclusives pour maximiser vos revenus",
      features: ["Alertes en temps réel", "Signaux privés", "Analyses avancées"],
      duration: "Mensuel",
      gradient: "from-violet-600 to-indigo-600",
      icon: <Sparkles className="w-6 h-6 text-white" />,
      price: "50€/mois"
    },
    {
      title: "Communauté Pro",
      description: "Rejoignez notre communauté d'experts et créateurs",
      features: ["Brainstorming quotidien", "Sessions Q&A", "Networking VIP"],
      duration: "Hebdomadaire",
      gradient: "from-blue-600 to-cyan-600",
      icon: <Users className="w-6 h-6 text-white" />,
      price: "30€/mois"
    },
    {
      title: "Premium Plus",
      description: "Notre offre la plus complète et personnalisée",
      features: ["Coaching personnalisé", "Support prioritaire", "Accès illimité"],
      duration: "Trimestriel",
      gradient: "from-pink-600 to-rose-600",
      icon: <Shield className="w-6 h-6 text-white" />,
      price: "75€/mois"
    }
  ], []);

  // Statistics data - Utilisent les données du contexte et les transactions réussies
  const statistics = useMemo(() => {
    // Vérifier si userProducts est un objet et le compter, sinon afficher 0
    let userProduits = 0;
    if (userProducts && typeof userProducts === 'object') {
      userProduits = Object.keys(userProducts).length;
    }
    
    return [
      { title: "Total des revenus", value: `${totalRevenue.toLocaleString()} F`, icon: <DollarSign className="w-4 h-4 text-emerald-500" />, color: "bg-emerald-100" },
      { title: "Abonnements", value: `${successfulTransactions}`, icon: <Users className="w-4 h-4 text-blue-500" />, color: "bg-blue-100" },
      { title: "Produits", value: `${userProduits}`, icon: <ShoppingBag className="w-4 h-4 text-purple-500" />, color: "bg-purple-100", fullWidth: true }
    ];
  }, [userProducts, successfulTransactions, totalRevenue]);

  // Handle quick action click - pas de restriction pour type_plan gratuit
  const handleQuickActionClick = useCallback((action) => {
    // Si l'utilisateur a un plan gratuit ou si c'est une action accessible à tous, naviguer normalement
    if (userInfos?.type_plan === "gratuit" || ["Statistiques", "Groupes", "Produits", "Support", "FAQ"].includes(action.label)) {
      navigate(handleNavigation(action.label));
    } else {
      // Pour les utilisateurs payants et fonctionnalités premium
      setShowAlert(true);
    }
  }, [navigate, handleNavigation, userInfos?.type_plan]);

  // Configure Telegram WebApp
  // Effet pour surveiller le défilement uniquement - Telegram désactivé
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ne pas utiliser Telegram du tout
  const isTelegramWebApp = false;
  
  // Aucune configuration Telegram nécessaire
  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 pb-20">
      {/* Simple title component */}
      <div className="max-w-lg mx-auto px-4">
        <AppTitle userInfos={userInfos} />
      </div>
      
      <main className="max-w-lg mx-auto px-4">
        {/* Welcome section */}
        <section className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Hello {userInfos?.nom || 'Utilisateur'}
              </h1>
             
            </div>
            <div className="flex items-center gap-2">
             
            </div>
          </div>
        </section>
        
        {/* Balance card based on user plan */}
        {userInfos && (
          <BalanceCard 
            userInfos={userInfos} 
            onWithdrawClick={() => navigate('/retrait')} 
          />
        )}

        {/* Coming soon alert modal */}
        {showAlert && <ComingSoonAlert onClose={() => setShowAlert(false)} />}

        {/* Statistics cards */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Statistiques</h2>
            <button 
             // onClick={() => navigate('/Statistics')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Détails <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {statistics.map((stat, index) => (
              <StatCard key={index} {...stat} />
            ))}
          </div>
        </section>

        {/* Quick actions section with enhanced animations */}
       {
        /*
         <section className="mb-8">
          <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Actions Rapides</h2>
              <button 
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-all duration-200 hover:translate-x-1"
                aria-label="More actions"
              >
                Voir tout
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {quickActions.map((action, index) => (
                <QuickActionButton
                  key={index}
                  icon={action.icon}
                  label={action.label}
                  gradient={action.gradient}
                  onClick={() => handleQuickActionClick(action)}
                />
              ))}
            </div>
          </div>
        </section>
        */
       }

        {/* Transactions history */}
        <section className="mb-8">
          <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Transactions Récentes</h2>
              <button 
                onClick={() => navigateTo('/transactions/history')}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-all duration-200 hover:translate-x-1"
              >
                Historique
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-2">
              {transactions.length > 0 ? (
                transactions.map((transaction, index) => (
                  <TransactionItem key={index} {...transaction} />
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 mb-1">Aucune transaction récente</p>
                  <p className="text-sm text-gray-400">Les transactions apparaîtront ici lorsque vous recevrez des paiements</p>
                </div>
              )}
            </div>
            
            {transactions.length > 0 && (
              <button 
                onClick={() => navigateTo('/transactions/history')}
                className="w-full mt-4 py-2 text-center text-blue-600 font-medium border border-blue-100 rounded-xl hover:bg-blue-50 transition-all duration-200"
              >
                Voir toutes les transactions
              </button>
            )}
          </div>
        </section>

        {/* Premium offers carousel with enhanced designs */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Offres Premium</h2>
            <button 
              onClick={() => navigate('/plans')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-all duration-200 hover:translate-x-1"
            >
              Voir les plans <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <Carousel>
            {premiumOffers.map((offer, index) => (
              <OfferCard
                key={index}
                {...offer}
                onDiscoverClick={() => {
                  //navigate('/plans')
                  console.log()
                }


                }
              />
            ))}
          </Carousel>
        </section>
        
        {/* Community section */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-x-10 -translate-y-20 blur-xl" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-pink-500/20 rounded-full translate-x-5 translate-y-10 blur-xl" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold">Rejoignez notre communauté</h2>
              </div>
              
              <p className="text-white/90 mb-6">
                Connectez-vous avec d'autres créateurs, partagez vos expériences et développez votre audience grâce à notre communauté active.
              </p>
              
              <div className="flex items-center gap-4">
      <a 
        href="https://t.me/kappelachannel" 
        target="_blank" 
        rel="noopener noreferrer"
        className="px-5 py-3 bg-white text-indigo-700 font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 inline-block text-center"
      >
        Rejoindre
      </a>
      <a 
        href="https://t.me/kappelachannel" 
        target="_blank" 
        rel="noopener noreferrer"
        className="px-5 py-3 bg-white/10 border border-white/30 text-white font-medium rounded-xl hover:bg-white/20 transition-all duration-300 inline-block text-center"
      >
        En savoir +
      </a>
    </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-2 z-30">
        <div className="max-w-lg mx-auto flex justify-between">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`flex flex-col items-center p-2 ${activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <DashboardIcon className="w-6 h-6" />
            <span className="text-xs mt-1">Accueil</span>
          </button>
          
          <button 
            onClick={() => navigate('/ProductsList')}
            className={`flex flex-col items-center p-2 ${activeTab === 'products' ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <ShoppingBag className="w-6 h-6" />
            <span className="text-xs mt-1">Produits</span>
          </button>
          
          <button 
            onClick={() => navigate('/GroupeListe')}
            className={`flex flex-col items-center p-2 ${activeTab === 'groups' ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <Users className="w-6 h-6" />
            <span className="text-xs mt-1">Groupes</span>
          </button>
          
          <button 
            onClick={() => navigate('/Statistics')}
            className={`flex flex-col items-center p-2 ${activeTab === 'stats' ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <BarChart3 className="w-6 h-6" />
            <span className="text-xs mt-1">Stats</span>
          </button>
          
          <button 
            onClick={() => navigate('/PaymentsSheetsIndex')}
            className={`flex flex-col items-center p-2 ${activeTab === 'settings' ? 'text-blue-600' : 'text-gray-500'}`}
          >
            <Settings className="w-6 h-6" />
            <span className="text-xs mt-1">Réglages</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default IndexEspace;